import { useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface SegmentedOption<V extends string> {
  readonly value: V;
  readonly label: ReactNode;
  /** Shown after the label in 500 / fg-3 (e.g. status counts). */
  readonly count?: ReactNode;
  readonly disabled?: boolean;
}

export interface SegmentedProps<V extends string> {
  readonly options: ReadonlyArray<SegmentedOption<V>>;
  readonly value: V;
  readonly onChange: (value: V) => void;
  /** Accessible name of the radiogroup, e.g. "Status". */
  readonly label: string;
  /** Lets the options wrap onto further rows on narrow screens (the pill grows from 32px). */
  readonly wrap?: boolean;
  readonly className?: string;
}

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const PREV_KEYS = new Set(['ArrowLeft', 'ArrowUp']);

/**
 * Pill segmented control used by every list screen (Status filters): role=radiogroup, 32px,
 * 2px padding, border-1, bg-2; the checked option is bg-3 with shadow-sm and an inset border
 * ring. Arrow keys move the selection (radio semantics, roving tabindex).
 */
export function Segmented<V extends string>({
  options,
  value,
  onChange,
  label,
  wrap,
  className,
}: SegmentedProps<V>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = options.filter((o) => !o.disabled);
  const checkedIndex = options.findIndex((o) => o.value === value);
  const focusIndex = checkedIndex >= 0 ? checkedIndex : options.findIndex((o) => !o.disabled);

  const move = (from: number, step: 1 | -1) => {
    if (enabled.length === 0) return;
    let i = from;
    for (let n = 0; n < options.length; n++) {
      i = (i + step + options.length) % options.length;
      const option = options[i];
      if (option && !option.disabled) {
        onChange(option.value);
        refs.current[i]?.focus();
        return;
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (NEXT_KEYS.has(event.key)) {
      event.preventDefault();
      move(index, 1);
    } else if (PREV_KEYS.has(event.key)) {
      event.preventDefault();
      move(index, -1);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'flex items-center gap-0.5 rounded-2 border border-border-1 bg-bg-2 p-0.5',
        wrap ? 'min-h-8 flex-wrap' : 'h-8',
        className,
      )}
    >
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={option.disabled}
            tabIndex={index === focusIndex ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg border-0 px-2.5 text-[12px] font-semibold',
              'disabled:cursor-not-allowed disabled:opacity-40',
              checked ? 'bg-bg-3 text-fg-1 shadow-seg' : 'bg-transparent text-fg-2',
            )}
          >
            {option.label}
            {/* The space only reaches the accessible name ("Sent 1"); the flex gap draws the spacing. */}
            {option.count !== undefined && ' '}
            {option.count !== undefined && <span className="font-medium text-fg-3">{option.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
